<template>
  <el-row class="app-header-bar">
    <el-col :span="14" class="app-header-title">
      <h3 v-if="project">
        {{ $filters.truncate(project.title, 40) }}
        <span
          class="project-status"
          :style="{
            '--color':
              statusColors[project.status as 'Draft' | 'Live' | 'Closed'],
          }"
        >
          {{ project.status }}
        </span>
      </h3>
      <p v-else />
    </el-col>
    <el-col :span="10">
      <el-menu
        ref="menu"
        class="top-menu"
        mode="horizontal"
        :background-color="'var(--app-header-bg, #293b4e)'"
        text-color="#fff"
        :vue-router="true"
        active-text-color="#ddd"
        :default-active="activeIndex"
        @select="handleSelect"
      >
        <el-menu-item
          id="menu-deactivate"
          index="menu-deactivate"
          style="display: none"
        />
        <el-menu-item
          index="projects"
          route="projects"
          title="List Projects (Alt+p shortcut)"
        >
          <icons-projects />&nbsp;
        </el-menu-item>
        <el-sub-menu index="recentProjects" title="Recent Projects">
          <template #title>
            <icons-recentprojects />
          </template>
          <el-menu-item
            v-if="!recentlyVisitedProjects.length"
            index="recentProjects-empty"
          >
            No Recent Projects
          </el-menu-item>
          <template v-else>
            <el-menu-item index="recentProjects-title" disabled>
              <span style="font-size: 90%">Recently Visited Projects:</span>
            </el-menu-item>
            <template
              v-for="p in recentlyVisitedProjects"
              :key="'recentProjects-' + p.id"
            >
              <el-menu-item
                :index="'recent-project-' + p.id"
                @click="(item) => goToProject(p)"
              >
                <icons-projects /> {{ p.title }}
              </el-menu-item>
            </template>
          </template>
        </el-sub-menu>
        <el-sub-menu index="moreActions" title="More actions">
          <template #title>
            <i-ep-operation />
          </template>
          <el-menu-item index="globalSearch" route="globalSearch">
            <icons-search />
            <span title="Shortcut: Alt + /"> Search across Projects </span>
          </el-menu-item>
          <el-menu-item index="newProject" route="newProject">
            <icons-new /> New Project
          </el-menu-item>
          <el-menu-item index="globalAuditEvents" route="globalAuditEvents">
            <icons-audit /> Activity
          </el-menu-item>
          <el-menu-item index="categories" route="categories">
            <icons-cooperation /> Categories
          </el-menu-item>
          <el-menu-item
            v-if="hasPermission('manageOrganisation')"
            index="organisations"
            route="organisations"
          >
            <icons-grid /> Organisations
          </el-menu-item>
          <el-menu-item index="responses" route="responses">
            <icons-respondent /> Responses
          </el-menu-item>
          <el-menu-item index="network" route="network">
            <icons-graph />
            Industry Network
          </el-menu-item>
          <el-menu-item index="webhooks" route="webhooks">
            <icons-webhooks /> Webhooks
          </el-menu-item>
          <el-menu-item index="tags" route="tags">
            <icons-tags /> Tags
          </el-menu-item>
          <el-menu-item index="users" route="users">
            <icons-user /> Users
          </el-menu-item>
          <el-menu-item index="FsmWorkflows" route="fsm">
            <icons-guide /> Workflow Rules
          </el-menu-item>
          <el-menu-item index="refHome" route="cms">
            <icons-reading /> Document Management
          </el-menu-item>
          <el-menu-item index="theme" route="theme">
            <icons-orange /> Theme
          </el-menu-item>
          <el-menu-item index="dashboard" route="dashboard">
            <icons-chart /> Dashboard
          </el-menu-item>
        </el-sub-menu>
        <el-sub-menu index="userInfo" title="User Info">
          <template #title>
            <i-ep-user />
            {{ activeUser.fullname }}
          </template>
          <!-- For My Account and Logout, use the index property to trigger navigation instead -->
          <el-menu-item index="my-account"> My Account </el-menu-item>
          <el-menu-item index="logout"> Logout </el-menu-item>
        </el-sub-menu>
      </el-menu>
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { uniqBy } from 'lodash-es'

import { ElMessageBox } from 'element-plus'

import { User, FullProject } from '@/v31-buyer/types.gen'
import { useAuthApiStore } from '../../shared/auth/authApiStore'
import { useUserProfileStore } from '../store/userProfile'
import { useProjectStore } from '../store/project/projectInstance'

const projectStore = useProjectStore()
const authStore = useUserProfileStore()
const authApiStore = useAuthApiStore()
const router = useRouter()
const route = useRoute()

// Data properties
const activeIndex = computed(() => String(route.name))
const statusColors = {
  Draft: 'orange',
  Live: 'palegreen',
  Closed: 'indianred',
}
interface RecentProject {
  id: number
  title?: string
}
const recentlyVisitedProjects = ref<RecentProject[]>([])

// Computed properties
const project = computed<FullProject>(() => projectStore.activeProject)
const activeUser = computed(() => authStore.currentUser)

// Methods
function hasPermission(permission: string) {
  return authStore.hasPermission(permission)
}

function goToProject(recentProject: RecentProject) {
  const isInProjDetailPage = !!route.params.projectId
  const notInSection = !route.params.sectionId
  const routeName =
    isInProjDetailPage && notInSection ? route.name : 'projectDetails'
  router.push({ name: routeName, params: { projectId: recentProject.id } })
}

function updateRecentProjects(
  projPayload: RecentProject,
  mode?: 'insert' | 'delete'
) {
  const maximum_stored_recent_projects = 6
  let recentProjects: RecentProject[] = JSON.parse(
    localStorage.getItem('recentProjects') || '[]'
  )

  if (mode === 'insert' && projPayload.id) {
    // add item to top
    recentProjects.unshift(projPayload)
  }

  if (mode === 'delete' && projPayload.id) {
    // delete existing item
    const existingIndex = recentProjects.findIndex(
      (x) => x.id == projPayload.id
    )
    if (existingIndex > -1) recentProjects.splice(existingIndex, 1)
  }

  // remove duplicates and limit stored items
  recentProjects = uniqBy(recentProjects, 'id')
  recentProjects = recentProjects.slice(0, maximum_stored_recent_projects)
  localStorage.setItem('recentProjects', JSON.stringify(recentProjects))
}

function updateRecentProjectsList() {
  const recentProjects: RecentProject[] = JSON.parse(
    localStorage.getItem('recentProjects') || '[]'
  )
  recentlyVisitedProjects.value = recentProjects.filter(
    (p) => p.id !== project.value?.id
  )
}

function handleSelect(index: string) {
  if (index === 'menu-deactivate') return // ignore hidden item

  // Handle special menu items
  if (index === 'my-account') {
    onMyAccountClicked(activeUser.value)
    return
  }
  if (index === 'logout') {
    onLogoutClicked()
    return
  }

  // Handle recent projects
  if (index.startsWith('recent-project-')) {
    const projectId = Number(index.replace('recent-project-', ''))
    const project = recentlyVisitedProjects.value.find(
      (p) => p.id === projectId
    )
    if (project) {
      goToProject(project)
    }
    return
  }

  // Default routing behavior
  if (index) {
    router.push({ name: index })
  }
}

function onLogoutClicked() {
  ElMessageBox.alert(
    'We will now redirect you to login page',
    'You have logged out',
    {
      confirmButtonText: 'OK',
      callback: () => {
        authApiStore.logout()
        router.push('/login')
      },
    }
  )
}

function onMyAccountClicked(user: User) {
  const params = {
    mode: 'edit',
    userId: user.id,
  }

  router.push({
    name: 'userForm',
    params,
  })
}

// Watchers - now directly watch projectStore state
watch(project, (newVal: FullProject, oldVal) => {
  if (newVal && newVal.id !== oldVal?.id) {
    // Project loaded - add to recent projects
    const recentProject: RecentProject = { id: newVal.id, title: newVal.title }
    updateRecentProjects(recentProject, 'insert')
  }
  updateRecentProjectsList()
})

onMounted(() => {
  updateRecentProjectsList()
})
</script>

<style lang="scss" scoped>
.top-menu {
  display: flex;
  justify-content: flex-end;

  .el-menu-item.is-active {
    background-color: rgba(0, 0, 0, 0.5);
  }
}

.app-header-bar {
  background: var(--app-header-bg, #293b4e); // Use CSS variable with fallback
  color: #eee;
  flex: 0 1 auto;
}

.app-header-title {
  padding-left: 10px;
}

.project-status {
  --color: black;
  color: var(--color);
  font-style: italic;
}

.el-menu--popup .el-menu-item svg {
  margin-right: 5px;
}
</style>
